















































































































import { Vue, Component, Watch } from 'vue-property-decorator';
import ApiHelper from 'api-helper';
import { library, dom } from '@fortawesome/fontawesome-svg-core';
import { faPlus, faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import Draggable from 'vuedraggable';
import LodashSome from 'lodash/some';

// Utils
import Toast from '@common/helpers/toast';
import {
  getQuestionLocalId,
  isQuestionLocalId,
} from '@/views/question/helpers';
import { State } from 'vuex-class';
// Components
import ContentLayout from '@components/layouts/Content.vue';
import QuestionListItem from '@/views/question/components/QuestionListItem/Index.vue';
import FormAction from '@/components/common/FormAction.vue';
import PageLoader from '@components/common/PageLoader.vue';
import BaseInput from '@/components/common/BaseInput.vue';
// Constants
import { Roles } from '@/common/app.config';
import { QUESTION_TYPES } from '@/views/question/constants';
import {
  createDefaultSubQuestionConfig,
  ISubQuestionConfig,
  mapDisplayConditionsFromBackend,
  mergeChildDisplayConditionsIntoParentConfig,
  hydrateParentAnswerMappingOnLoad,
  buildAnswerMappingForApiSave,
  resolveSaveDepth,
  stripMappingDuplicateConditions,
  validateSubQuestionsBeforeSave,
  syncSubQuestionIdsFromParentLinks,
  buildDisplayConditionsForChildQuestion,
  buildChildDisplayConditions,
  findParentQuestionForChild,
  normalizeConditionAnswerValues,
  hasExplicitGlobalConditionForAnswer,
} from '@/views/question/constants/sub-question';
// Icons
import ListIcon from '@/assets/img/icon-diagnosis.svg';
import { cloneDeep, remove, unionBy } from 'lodash';

@Component({
  components: {
    ContentLayout,
    QuestionListItem,
    Draggable,
    FormAction,
    PageLoader,
    BaseInput,
  },
})
export default class QuestionListEdit extends Vue {
  @State((state) => state.auth.currentUser) currentUser!: any;

  iconHeader: any = ListIcon;
  isSubmiting: boolean = false;
  isLoading: boolean = false;
  isChanged: boolean = false;
  questionOptionEditting: string = '';
  countManualSubscription: number | null = null;
  countAutoSubscription: number | null = null;

  questions: any[] = [];
  questionIdsDelete: number[] = [];
  validOptions: any[] = [];
  validOptionsUnionBy: any[] = [];

  get isAdmin() {
    return this.currentUser && this.currentUser.role === Roles.superAdmin;
  }

  get diagnosisId() {
    return this.$route.params.id;
  }

  get refQuestionList() {
    return this.$refs.questionList as any;
  }

  get computedForm() {
    return this.$refs.questionForm as any;
  }

  get dragOptions() {
    return {
      animation: 200,
      disabled: this.questions.length === 1 || !this.isAdmin,
      handle: '.handle',
      forceFallback: true,
      scrollSensitivity: 200,
    };
  }

  async created() {
    this.isLoading = true;
    await this.getDiagnosisDetail();
    await this.getQuestions();
    this.isLoading = false;
    library.add(faPlus);
    library.add(faPlusCircle);
    dom.watch();
  }

  updateIsSubmiting(isSubmiting) {
    this.isSubmiting = isSubmiting;
  }

  updateIsChanged(status) {
    this.isChanged = status;
  }

  clickEdit(id) {
    this.questionOptionEditting = id;
  }

  scrollBottomQuestionItem() {
    this.$nextTick(() => {
      const questionNodes = this.refQuestionList.$children;
      const lastQuestionNode = questionNodes[questionNodes.length - 1];
      lastQuestionNode.$el.scrollIntoView({ behavior: 'smooth' });
    });
  }

  handleUpdateQuestion(index, name, value, item) {
    if (name === 'count_number') {
      const newValue = {
        ...this.questions[index],
        options: [{ count_number: parseInt(value, 10) }],
      };

      this.$set(this.questions, index, newValue);
      return;
    }

    const { INPUT, TEXTAREA, CHECKBOX, RADIO } = QUESTION_TYPES;
    const newValue = {
      ...this.questions[index],
      [name]: value,
    };

    if (name === 'type' && [TEXTAREA, INPUT].includes(value)) {
      newValue.options = [{ count_number: 0 }];
      remove(this.validOptionsUnionBy, (i) => i.id === item.id);
    }

    if (name === 'type' && [CHECKBOX, RADIO].includes(value)) {
      newValue.placeholder = '';
      newValue.options = [{ value: '', is_call_video: false, count_number: 0 }];
      this.questionOptionEditting = `${item.id}_0`;
    }
    this.$set(this.questions, index, newValue);
  }

  handleDeleteQuestion(id: number | string) {
    // Capture the deleted question's direct child IDs from sub_question_ids BEFORE removal.
    // Locally created questions never have parent_question_id set on them (only the parent's
    // sub_question_config.sub_question_ids is updated). Step 1 must also check sub_question_ids
    // so that locally created children are correctly orphaned when their parent is deleted.
    const deletedQuestion = this.questions.find((q) => String(q.id) === String(id));
    const directChildIdsFromSubConfig = new Set<string>(
      (deletedQuestion?.sub_question_config?.sub_question_ids || []).map((cid) => String(cid)),
    );

    // Step 1: Orphan direct children of the deleted question.
    // Check both parent_question_id (API-loaded) and sub_question_ids (locally created).
    const orphanedIds = new Set<string | number>();
    this.questions.forEach((q) => {
      const isChildByParentLink = String(q.parent_question_id) === String(id);
      const isChildBySubConfig = directChildIdsFromSubConfig.has(String(q.id));
      if (isChildByParentLink || isChildBySubConfig) {
        q.parent_question_id = null;
        if (q.sub_question_config) {
          q.sub_question_config.level = 1;
        }
        orphanedIds.add(q.id);
      }
    });

    // Step 2: Remove deleted question from the list.
    const newQuestions = this.questions.filter((x) => x.id !== id);
    this.questions = newQuestions;

    // Step 3: Recalculate depth and level ONLY for descendants of the deleted question.
    // Recalculating ALL questions would reset depth=0 for any sub-question whose
    // parent_question_id is null/undefined (e.g. locally created, not yet saved).
    // Only orphaned children and their subtrees have stale depths after a parent deletion.
    const affectedIds = new Set<string>();
    orphanedIds.forEach((orphanId) => affectedIds.add(String(orphanId)));

    const collectDescendants = (parentId: string | number) => {
      const parentQ = newQuestions.find((q) => String(q.id) === String(parentId));
      const childIdsFromParentSubConfig = new Set<string>(
        (parentQ?.sub_question_config?.sub_question_ids || []).map((cid) => String(cid)),
      );
      newQuestions.forEach((q) => {
        const isChildByParentLink = String(q.parent_question_id) === String(parentId);
        const isChildBySubConfig = childIdsFromParentSubConfig.has(String(q.id));
        if ((isChildByParentLink || isChildBySubConfig) && !affectedIds.has(String(q.id))) {
          affectedIds.add(String(q.id));
          collectDescendants(q.id);
        }
      });
    };
    orphanedIds.forEach((orphanId) => collectDescendants(orphanId));

    if (affectedIds.size > 0) {
      const depthCache = new Map<string, number>();
      const levelCache = new Map<string, number>();

      const getDepth = (qId: string | number): number => {
        const key = String(qId);
        if (depthCache.has(key)) {
          return depthCache.get(key)!;
        }
        const q = newQuestions.find((x) => String(x.id) === key);
        if (!q || q.parent_question_id == null) {
          depthCache.set(key, 0);
          return 0;
        }
        const d = getDepth(q.parent_question_id) + 1;
        depthCache.set(key, d);
        return d;
      };

      const getLevel = (qId: string | number): number => {
        const key = String(qId);
        if (levelCache.has(key)) {
          return levelCache.get(key)!;
        }
        const q = newQuestions.find((x) => String(x.id) === key);
        if (!q || q.parent_question_id == null) {
          levelCache.set(key, 1);
          return 1;
        }
        const parentLevel = getLevel(q.parent_question_id);
        const l = Math.min(parentLevel + 1, 5); // Cap at MAX_LEVEL
        levelCache.set(key, l);
        return l;
      };

      newQuestions
        .filter((q) => affectedIds.has(String(q.id)))
        .forEach((q) => {
          q.depth = getDepth(q.id);
          if (q.sub_question_config) {
            q.sub_question_config.level = getLevel(q.id);
          }
        });
    }

    if (!isQuestionLocalId(id)) {
      this.questionIdsDelete.push(Number(id));
    }
  }

  handleAddQuestion() {
    this.questions.push({
      id: getQuestionLocalId(),
      description: '',
      multiple: false,
      options: [{ count_number: 0 }],
      order: this.questions.length,
      placeholder: '',
      question: '',
      required: !(this.questions.length > 0),
      count_ng_required: false,
      type: QUESTION_TYPES.INPUT,
      woman_only: false,
      sub_question_config: createDefaultSubQuestionConfig(),
    });
    this.scrollBottomQuestionItem();
  }

  handleAddOption(index: number) {
    const question = this.questions[index];
    const newOptionIndex = question.options.length;
    const newValue = {
      ...question,
      options: [...question.options, { value: '', is_call_video: false, count_number: 0 }],
    };
    this.$set(this.questions, index, newValue);
    this.questionOptionEditting = `${question.id}_${newOptionIndex}`;
  }

  handleUpdateOption(index: number, optionIndex: number, value: any) {
    this.questionOptionEditting = '';
    const question = this.questions[index];
    const oldOption = question.options[optionIndex];
    const options = question.options.map((x: any, i: number) => {
      if (i === optionIndex) {
        return {
          is_call_video: false,
          ...value,
        };
      }
      return x;
    });

    const newValue = {
      ...question,
      options,
    };

    // If option value changed, migrate the sub-question mapping and conditions
    if (oldOption && oldOption.value !== value.value && newValue.sub_question_config) {
      const mapping = newValue.sub_question_config.answer_mapping;
      if (mapping && mapping[oldOption.value]) {
        mapping[value.value] = mapping[oldOption.value];
        delete mapping[oldOption.value];
      }
      // Sync answer_value in conditions so the condition modal shows the updated label
      if (newValue.sub_question_config.conditions) {
        newValue.sub_question_config.conditions = newValue.sub_question_config.conditions.map(
          (c: any) => {
            const condValues: any[] = Array.isArray(c.answer_value)
              ? c.answer_value
              : [c.answer_value];
            const hasOldValue = condValues.some(
              (v: any) => String(v) === String(oldOption.value),
            );
            if (!hasOldValue) { return c; }
            const newValues = condValues.map((v: any) =>
              String(v) === String(oldOption.value) ? value.value : v,
            );
            return {
              ...c,
              answer_value: Array.isArray(c.answer_value) ? newValues : newValues[0],
            };
          },
        );
      }
    }

    this.$set(this.questions, index, newValue);
  }

  handleDeleteOption(index: number, optionIndex: number) {
    const question = this.questions[index];
    const deletedOption = question.options[optionIndex];
    const options = question.options.filter((_: any, i: number) => i !== optionIndex);
    const newValue = {
      ...question,
      options,
    };

    // Clean up mapping and conditions for the deleted option
    if (deletedOption && newValue.sub_question_config) {
      if (newValue.sub_question_config.answer_mapping) {
        delete newValue.sub_question_config.answer_mapping[deletedOption.value];
      }

      // Remove conditions that reference the deleted option's value to prevent
      // stale entries from appearing in the condition modal.
      if (newValue.sub_question_config.conditions) {
        newValue.sub_question_config.conditions = newValue.sub_question_config.conditions.filter(
          (c: any) => {
            if (!['is', 'in', 'not_is', 'not_in'].includes(c.comparison)) { return true; }
            const condValues: any[] = Array.isArray(c.answer_value)
              ? c.answer_value
              : [c.answer_value];
            return !condValues.some(
              (v: any) => String(v) === String(deletedOption.value),
            );
          },
        );
      }

      // Rebuild sub_question_ids from both answer_mapping (per-answer) and original global selections.
      // conditions[].target_question_id is the parent question ID (not a sub-question ID)
      // and must not be included here.
      const allMappedIds = Object.values(
        newValue.sub_question_config.answer_mapping || {},
      ).flat();

      // Preserve global sub-question IDs (those selected via global selector, not per-answer mapping).
      const currentGlobalIds = newValue.sub_question_config.sub_question_ids || [];
      const remainingIds = Array.from(new Set([...allMappedIds, ...currentGlobalIds]));
      newValue.sub_question_config.sub_question_ids = remainingIds.map((id: any) =>
        typeof id === 'number' ? id : (isQuestionLocalId(id) ? id : Number(id)),
      );
    }

    this.$set(this.questions, index, newValue);
  }

  handleCancel() {
    return this.$router.push({
      name: 'diagnosis.detail',
      params: { id: this.diagnosisId },
    });
  }

  handleUpdateSubConfig(index: number, config: ISubQuestionConfig) {
    const question = this.questions[index];
    const oldSubIds = question.sub_question_config?.sub_question_ids || [];
    const newSubIds = config.sub_question_ids || [];

    // Detect when sub-question IDs are removed from parent config.
    // When admin deselects a sub-question, clear its parent_question_id.
    // (depth/level are preserved — admin can override via dropdown, see Bug #5).
    const removedIds = oldSubIds.filter(
      (id) => !newSubIds.some((newId) => String(newId) === String(id)),
    );

    removedIds.forEach((removedId) => {
      const childIdx = this.questions.findIndex(
        (q) => String(q.id) === String(removedId),
      );
      if (childIdx !== -1) {
        // Clear parent_question_id only — depth/level are kept as-is so admin
        // can freely override them via the level dropdown without the save formula
        // (Math.max(graphDepth, levelDepth)) pulling depth back from the parent graph.
        this.$set(this.questions, childIdx, {
          ...this.questions[childIdx],
          parent_question_id: null,
          display_conditions: null,
        });
      }
    });

    const newValue: any = {
      ...question,
      sub_question_config: config,
    };
    if ((config.sub_question_ids || []).length === 0) {
      newValue.display_conditions = null;
    }

    // Bug #14: Detect when answer values are removed from global conditions.
    // Compare OLD vs NEW conditions to find answer values that were removed.
    // This identifies children that are "fully orphaned" (all trigger answers deleted).
    // answer_mapping is NOT changed — the (+) modal still correctly ticks subs after rule removal.
    const oldConditionAnswerValues = new Set<string>(
      (question.sub_question_config?.conditions || [])
        .flatMap((c: any) => normalizeConditionAnswerValues(c.answer_value))
        .filter(Boolean) as string[],
    );
    const newConditionAnswerValues = new Set<string>(
      (config.conditions || [])
        .flatMap((c: any) => normalizeConditionAnswerValues(c.answer_value))
        .filter(Boolean) as string[],
    );
    const removedFromConditions = Array.from(oldConditionAnswerValues).filter(
      (a) => !newConditionAnswerValues.has(a),
    );

    // Child "fully orphaned" khi TẤT CẢ trigger answers của nó bị xoá khỏi global conditions.
    // Ví dụ: S2 mapped B, B bị xoá → fullyOrphaned. S3 mapped (A, B), chỉ B bị xoá → không orphan.
    const fullyOrphanedChildIds = new Set<string>();
    if (removedFromConditions.length > 0) {
      (config.sub_question_ids || []).forEach((childId) => {
        const triggerAnswers = Object.keys(config.answer_mapping || {}).filter(
          (ansValue) =>
            ((config.answer_mapping || {})[ansValue] || []).some(
              (id) => String(id) === String(childId),
            ),
        );
        if (
          triggerAnswers.length > 0
          && triggerAnswers.every((ans) => removedFromConditions.includes(ans))
        ) {
          fullyOrphanedChildIds.add(String(childId));
        }
      });
    }

    // [SCOPE] Bug #10: xoá display_conditions stale trên child khi cha chưa có điều kiện modal.
    if ((config.conditions || []).length === 0) {
      (config.sub_question_ids || []).forEach((childId) => {
        const childIdx = this.questions.findIndex(
          (q) => String(q.id) === String(childId),
        );
        if (childIdx !== -1) {
          this.$set(this.questions, childIdx, {
            ...this.questions[childIdx],
            display_conditions: null,
          });
        }
      });
    } else {
      // Conditions không rỗng nhưng có thể đã thay đổi (thêm/xóa rule).
      // Rebuild display_conditions của mỗi child dựa trên parent conditions mới nhất.
      const defaultOperator = question.type === QUESTION_TYPES.CHECKBOX ? 'in' : 'is';
      (config.sub_question_ids || []).forEach((childId) => {
        const childIdx = this.questions.findIndex(
          (q) => String(q.id) === String(childId),
        );
        if (childIdx !== -1) {
          // [SCOPE] Bug #14: Child bị fully orphaned (trigger answers đều bị xoá khỏi conditions)
          // → display_conditions = null. answer_mapping giữ nguyên → modal (+) vẫn tick sub.
          const rebuiltConditions = fullyOrphanedChildIds.has(String(childId))
            ? null
            : buildChildDisplayConditions({
              childId,
              globalConditions: config.conditions || [],
              answerMapping: config.answer_mapping || {},
              defaultOperator,
              parentType: question.type,
            });
          this.$set(this.questions, childIdx, {
            ...this.questions[childIdx],
            display_conditions: rebuiltConditions,
          });
        }
      });
    }

    this.$set(this.questions, index, newValue);
    this.updateIsChanged(true);
  }

  async getDiagnosisDetail() {
    try {
      const res = await ApiHelper.getApi('MedicalMenuApi').getOne(this.diagnosisId);
      this.countAutoSubscription = res.count_auto_subscription;
      this.countManualSubscription = res.count_manual_subscription;
    } catch (error) {
      if (error.response) {
        Toast.error(error.response.data.message);
      }
    }
  }

  async getQuestions() {
    try {
      this.isLoading = true;
      const params = {
        menu_id: this.diagnosisId,
      };
      const { data } = await ApiHelper.getApi('QuestionApi').getList(params);

      const flattenedQuestions: any[] = [];
      const processedIds = new Set();

      const processList = (list: any[], parentId: string | number | null = null, depth = 0) => {
        if (!list || !Array.isArray(list)) {
          return;
        }
        list.forEach((q) => {
          if (processedIds.has(q.id)) {
            return;
          }
          processedIds.add(q.id);

          let options = q.options || [];
          if (typeof options === 'string') {
            try {
              options = JSON.parse(options);
            } catch (e) {
              options = [];
            }
          }
          if (!Array.isArray(options)) {
            options = options ? [options] : [];
          }
          options = options.map((o: any) =>
            typeof o === 'string' ? { value: o, is_call_video: false, count_number: 0 } : o,
          );

          const questionObj = {
            ...q,
            options,
            sub_question_config: createDefaultSubQuestionConfig(),
          };

          const apiDepth = typeof q.depth === 'number' ? q.depth : 0;
          questionObj.depth = apiDepth;
          questionObj.sub_question_config.level = apiDepth + 1;

          if (!q.parent_question_id && q.display_conditions) {
            const parentConditions = mapDisplayConditionsFromBackend(q.display_conditions);

            questionObj.sub_question_config.conditions = parentConditions.map((c: any) => ({
              ...c,
              target_question_id: q.id,
            }));
          }

          if (q.sub_questions && q.sub_questions.length > 0) {
            processList(q.sub_questions, q.id, apiDepth + 1);
          }

          delete questionObj.sub_questions;
          flattenedQuestions.push(questionObj);
        });
      };

      processList(data);

      // Phase 1: link children into parent sub_question_ids
      flattenedQuestions.forEach((q: any) => {
        if (q.parent_question_id || q.depth > 0) {
          const parent = flattenedQuestions.find((p: any) => String(p.id) === String(q.parent_question_id));
          if (parent) {
            if (!parent.sub_question_config.sub_question_ids) {
              parent.sub_question_config.sub_question_ids = [];
            }
            if (!parent.sub_question_config.sub_question_ids.some((sid: any) => String(sid) === String(q.id))) {
              parent.sub_question_config.sub_question_ids.push(q.id);
            }
          }
        }
      });

      const parentIds = new Set<string | number>();
      flattenedQuestions.forEach((q: any) => {
        if (q.parent_question_id) {
          parentIds.add(q.parent_question_id);
        }
      });

      // [SCOPE] Bug #8: reconcile answer_mapping TRƯỚC merge child display_conditions.
      // isMappingOnlyRule cần answer_mapping để skip mapping-only rules — nếu merge trước
      // thì mapping rỗng → điều kiện của child bị leak vào parent.conditions.
      parentIds.forEach((parentId) => {
        const parent = flattenedQuestions.find((p: any) => String(p.id) === String(parentId));
        if (!parent) {
          return;
        }
        const children = flattenedQuestions.filter(
          (q: any) => String(q.parent_question_id) === String(parentId),
        );
        parent.sub_question_config = hydrateParentAnswerMappingOnLoad(
          parent.sub_question_config,
          children,
          parent.answer_mapping,
          parent.type,
        );

      });

      // Phase 3: merge non-mapping-only child conditions into parent global modal state
      flattenedQuestions.forEach((q: any) => {
        if (!(q.parent_question_id || q.depth > 0) || !q.display_conditions) {
          return;
        }
        const parent = flattenedQuestions.find((p: any) => String(p.id) === String(q.parent_question_id));
        if (!parent) {
          return;
        }

        parent.sub_question_config = mergeChildDisplayConditionsIntoParentConfig({
          parentConfig: parent.sub_question_config,
          parentId: parent.id,
          childId: q.id,
          displayConditions: q.display_conditions,
        });
      });

      flattenedQuestions.forEach((q: any) => {
        if (q.sub_question_config) {
          q.sub_question_config = stripMappingDuplicateConditions(
            q.type,
            q.sub_question_config,
          );
        }
      });

      // [SCOPE] Bug #7: khi tất cả sub-questions đã bị xoá và submit lên API, display_conditions
      // của câu hỏi cha có thể vẫn còn trên API từ lần lưu trước. Nếu không có sub-questions
      // thì conditions không có ý nghĩa gì → clear để UI không hiển thị "1 件の条件設定済み".
      flattenedQuestions.forEach((q: any) => {
        if (
          q.sub_question_config
          && (q.sub_question_config.sub_question_ids || []).length === 0
          && (q.sub_question_config.conditions || []).length > 0
        ) {
          q.sub_question_config.conditions = [];
        }
      });

      this.questions = flattenedQuestions.sort((a: any, b: any) => a.order - b.order);

      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      if (error.response) {
        Toast.error(error.response.data.message);
      }
    }
  }

  async handleSubmit() {
    const valid = await this.computedForm.validate();

    if (!valid) {
      return;
    }

    let validOption: boolean = true;
    this.validOptionsUnionBy.map((item: any) => {
      if (!item.value) {
        validOption = false;
      }
    });

    if (!validOption) {
      return;
    }

    const syncedQuestions = syncSubQuestionIdsFromParentLinks(this.questions);

    const questions = syncedQuestions.map((x: any, i: number) => {
      const _isQuestionLocalId = isQuestionLocalId(x.id);
      const tempId = _isQuestionLocalId ? x.id : null;

      let parentQuestionId: string | number | null = null;
      let parentTempId: string | null = null;

      const depth = resolveSaveDepth(
        x.id,
        x.sub_question_config?.level || 1,
        syncedQuestions,
      );

      const parentQuestion = findParentQuestionForChild(x.id, syncedQuestions);
      let displayConditions = buildDisplayConditionsForChildQuestion(x, syncedQuestions);

      // Bug #14 (save path): handleUpdateSubConfig set display_conditions = null for fully-orphaned
      // children (trigger answer deleted from parent conditions). buildDisplayConditionsForChildQuestion
      // recomputes from mapping-only path and would override that null with a stale value.
      // Trust the null set by handleUpdateSubConfig when the child is truly orphaned.
      // Discriminator: 上記項目 children have non-null display_conditions after handleUpdateSubConfig,
      // so the x.display_conditions === null guard does not fire for them.
      if (x.display_conditions === null && displayConditions !== null && x.parent_question_id) {
        const orphanParent = findParentQuestionForChild(x.id, syncedQuestions);
        if (orphanParent?.sub_question_config) {
          const answerMapping = orphanParent.sub_question_config.answer_mapping || {};
          const parentGlobalConditions = orphanParent.sub_question_config.conditions || [];
          const triggerAnswers = Object.keys(answerMapping).filter((ans) =>
            (answerMapping[ans] || []).some((id) => String(id) === String(x.id)),
          );
          const isOrphaned =
            triggerAnswers.length > 0
            && parentGlobalConditions.length > 0
            && triggerAnswers.every(
              (ans) => !hasExplicitGlobalConditionForAnswer(parentGlobalConditions, ans),
            );
          if (isOrphaned) {
            displayConditions = null;
          }
        }
      }

      if (parentQuestion) {
        const isParentLocal = isQuestionLocalId(parentQuestion.id);
        parentQuestionId = isParentLocal ? null : Number(parentQuestion.id);
        parentTempId = isParentLocal ? String(parentQuestion.id) : null;
      } else if (x.parent_question_id) {
        const parentId = x.parent_question_id;
        const isParentLocal = parentId ? isQuestionLocalId(parentId) : false;
        parentQuestionId = isParentLocal ? null : Number(parentId);
        parentTempId = isParentLocal ? String(parentId) : null;
      }

      let multiple = x.multiple;
      if (x.type === QUESTION_TYPES.CHECKBOX) {
        multiple = true;
      }
      if (x.type === QUESTION_TYPES.RADIO) {
        multiple = false;
      }

      const { sub_question_config, ...cleanQuestion } = x;
      // Explicitly remove sub_questions to ensure payload is flat
      delete (cleanQuestion as any).sub_questions;

      const payloadQuestion: any = {
        ...cleanQuestion,
        multiple,
        description: cleanQuestion.description || '',
        placeholder: cleanQuestion.placeholder || '',
        temp_id: tempId,
        parent_question_id: parentQuestionId,
        parent_temp_id: parentTempId,
        display_conditions: displayConditions,
        answer_mapping: buildAnswerMappingForApiSave(x.sub_question_config, x.type),
        depth,
        order: i,
      };

      // Safeguard: If parent is not in the payload, set parent_question_id/temp_id to null
      // to avoid Foreign Key violation in the database
      const parentIdToCheck = parentQuestionId || parentTempId;
      if (parentIdToCheck) {
        const parentExists = syncedQuestions.some(
          (p) => String(p.id) === String(parentIdToCheck),
        );
        if (!parentExists) {
          payloadQuestion.parent_question_id = null;
          payloadQuestion.parent_temp_id = null;
          payloadQuestion.depth = 0;
        }
      }

      if (!_isQuestionLocalId) {
        payloadQuestion.id = x.id;
      } else {
        delete payloadQuestion.id;
      }

      return payloadQuestion;
    });

    const payload = {
      menu_id: Number(this.diagnosisId),
      ids_delete: this.questionIdsDelete,
      count_manual_subscription: !this.isDisabledInput ? Number(this.countManualSubscription) : null,
      count_auto_subscription: !this.isDisabledInput ? Number(this.countAutoSubscription) : null,
      questions: questions.map((q) => ({
        ...q,
        options: (Array.isArray(q.options) ? q.options : (q.options ? [q.options] : [])).map((opt) => {
          const countNum = parseInt(`${opt.count_number}`, 10);
          return {
            is_call_video: false,
            ...opt,
            value: opt.value || '',
            count_number: isNaN(countNum) ? 0 : countNum,
          };
        }),
      })),
    };

    const subQuestionErrors = validateSubQuestionsBeforeSave(syncedQuestions);
    if (subQuestionErrors.length > 0) {
      Toast.error(subQuestionErrors[0]);
      return;
    }

    try {
      this.updateIsSubmiting(true);
      await ApiHelper.getApi('QuestionApi').updateQuestions(payload);
      await this.getQuestions();
      this.questionIdsDelete = [];
      this.updateIsSubmiting(false);
      this.updateIsChanged(false);
      Toast.success('登録しました');
    } catch (error) {
      this.updateIsSubmiting(false);
      this.updateIsChanged(false);
      const data = error.response.data;
      Toast.error(data.message);
      if (error.response.status === 422) {
        this.computedForm.setErrors(error.response.data.errors);
        return;
      }
    }
  }

  checkValidOptions(event) {
    this.validOptions.push(event);
    const validOptionsReverse = cloneDeep(this.validOptions).reverse();
    this.validOptionsUnionBy = unionBy(validOptionsReverse, 'id');
  }

  @Watch('questions', { deep: true })
  private watchQuestions(_, oldValue) {
    this.WatchAssignValueForFistQuestion();
    if (this.isChanged || !oldValue.length) {
      return;
    }
    this.updateIsChanged(true);
  }

  private WatchAssignValueForFistQuestion() {
    if (!LodashSome( this.questions, 'required') && this.questions.length > 0) {
      this.questions[0].required = true;
    }
  }

  get rulesCountAutoSubscription() {
    return `required|onlyNumber|bigger_number:${this.countManualSubscription}`;
  }

  get optionErrorInputMessages() {
    return {
      max: '100文字以内で入力してください',
      numeric: '正の整数を入力してください',
      required: '手動診察カウント数を入力してください',
    };
  }

  get optionErrorNumberOfMedicalInputMessages() {
    return {
      max: '100文字以内で入力してください',
      numeric: '正の整数を入力してください',
      required: '診療不要定期便カウント数を入力してください',
    };
  }

  get isDisabledInput() {
    if (LodashSome( this.questions, 'count_ng_required')) {
      return false;
    } else {
      this.countManualSubscription = null;
      this.countAutoSubscription = null;
      return true;
    }
  }
}
